import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { List, Plus, Pencil, Trash, DotsSixVertical } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusesPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#457B9D',
    order: 0,
    is_default: false,
    is_closed: false
  });

  const colorPresets = [
    '#457B9D', '#2D6A4F', '#F4A261', '#D95D39', '#E63946', '#1E3A2B', '#8A8782'
  ];

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/statuses`);
      setStatuses(data);
    } catch (e) {
      toast.error('Failed to fetch statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStatus) {
        await axios.put(`${API_URL}/api/statuses/${editingStatus.id}`, formData);
        toast.success('Status updated');
      } else {
        await axios.post(`${API_URL}/api/statuses`, {
          ...formData,
          order: statuses.length
        });
        toast.success('Status created');
      }
      fetchStatuses();
      closeDialog();
    } catch (e) {
      toast.error('Failed to save status');
    }
  };

  const handleDelete = async (statusId) => {
    if (!window.confirm('Delete this status?')) return;
    try {
      await axios.delete(`${API_URL}/api/statuses/${statusId}`);
      toast.success('Status deleted');
      fetchStatuses();
    } catch (e) {
      toast.error('Failed to delete status');
    }
  };

  const openEditDialog = (status) => {
    setEditingStatus(status);
    setFormData({
      name: status.name,
      color: status.color,
      order: status.order,
      is_default: status.is_default,
      is_closed: status.is_closed
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingStatus(null);
    setFormData({ name: '', color: '#457B9D', order: 0, is_default: false, is_closed: false });
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Statuses</h1>
          <p className="text-muted-foreground">Configure record statuses</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => closeDialog()} data-testid="add-status-btn">
              <Plus size={18} className="mr-2" />
              Add Status
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingStatus ? 'Edit Status' : 'Add Status'}</DialogTitle>
              <DialogDescription>Configure status properties</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="In Progress"
                  required
                  data-testid="status-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className="w-16 h-10 p-1"
                    data-testid="status-color"
                  />
                  <div className="flex gap-1">
                    {colorPresets.map(color => (
                      <button
                        key={color}
                        type="button"
                        className="w-6 h-6 rounded-full border-2 border-transparent hover:border-foreground transition-colors"
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({...formData, color})}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                    id="is_default"
                    data-testid="status-default"
                  />
                  <Label htmlFor="is_default" className="text-sm">Default status for new records</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.is_closed}
                    onCheckedChange={(checked) => setFormData({...formData, is_closed: checked})}
                    id="is_closed"
                    data-testid="status-closed"
                  />
                  <Label htmlFor="is_closed" className="text-sm">Closed status (for reporting)</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button type="submit" data-testid="save-status-btn">
                  {editingStatus ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-6">
          {statuses.length > 0 ? (
            <div className="space-y-2">
              {statuses.map(status => (
                <div
                  key={status.id}
                  className="flex items-center justify-between p-4 bg-secondary/50 rounded-md hover:bg-secondary transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <DotsSixVertical size={16} className="text-muted-foreground cursor-grab" />
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="font-medium">{status.name}</span>
                    <div className="flex gap-2">
                      {status.is_default && (
                        <Badge variant="secondary">Default</Badge>
                      )}
                      {status.is_closed && (
                        <Badge variant="outline">Closed</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(status)} data-testid={`edit-status-${status.id}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(status.id)} className="text-destructive" data-testid={`delete-status-${status.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <List size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-muted-foreground">No statuses configured</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatusesPage;
